import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * Carousel — horizontal scrollable slide set built on embla-carousel.
 *
 * Use for: product feature carousels, recommendation cards, marketing
 * banners — anywhere a horizontal swipeable list is the right primitive.
 * Do NOT use for primary information that must be discoverable (carousels
 * hide content); use a stacked list or grid instead.
 *
 * The component exposes the embla API via context so apps can compose:
 *   <Carousel orientation="horizontal" opts={{ align: "start", loop: true }}>
 *     <CarouselContent>
 *       <CarouselItem>...</CarouselItem>
 *     </CarouselContent>
 *     <CarouselPrevious />
 *     <CarouselNext />
 *   </Carousel>
 */

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

export type CarouselOrientation = "horizontal" | "vertical";

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: CarouselApi;
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  selectedIndex: number;
  scrollSnaps: number[];
  orientation: CarouselOrientation;
};

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const ctx = React.useContext(CarouselContext);
  if (!ctx) {
    throw new Error("Carousel subcomponents must be used inside <Carousel>.");
  }
  return ctx;
}

export interface CarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: CarouselOrientation;
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  /** Receive a reference to the embla API instance. */
  setApi?: (api: CarouselApi) => void;
}

export const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(function Carousel(
  { children, className, opts, orientation = "horizontal", plugins, setApi, ...props },
  ref,
) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

  const onSelect = React.useCallback((carouselApi: CarouselApi) => {
    if (!carouselApi) return;
    setCanScrollPrev(carouselApi.canScrollPrev());
    setCanScrollNext(carouselApi.canScrollNext());
    setSelectedIndex(carouselApi.selectedScrollSnap());
  }, []);

  const scrollPrev = React.useCallback(() => api?.scrollPrev(), [api]);
  const scrollNext = React.useCallback(() => api?.scrollNext(), [api]);

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    setScrollSnaps(api.scrollSnapList());
    onSelect(api);
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (orientation === "horizontal") {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      } else {
        if (event.key === "ArrowUp") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          scrollNext();
        }
      }
    },
    [orientation, scrollNext, scrollPrev],
  );

  return (
    <CarouselContext.Provider
      value={{
        api,
        canScrollNext,
        canScrollPrev,
        carouselRef,
        orientation,
        scrollNext,
        scrollPrev,
        scrollSnaps,
        selectedIndex,
      }}
    >
      <div
        ref={ref}
        aria-roledescription="carousel"
        className={cx("cw-v2-carousel", `cw-v2-carousel--${orientation}`, className)}
        onKeyDownCapture={handleKeyDown}
        // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA carousel pattern uses <div role="region" aria-roledescription="carousel">; <section> would only act as a landmark when accessibly named, and aria-roledescription is a description, not a name.
        role="region"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
});
Carousel.displayName = "Carousel";

export const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function CarouselContent({ className, ...props }, ref) {
  const { carouselRef, orientation } = useCarousel();
  return (
    <div ref={carouselRef} className="cw-v2-carousel__viewport">
      <div
        ref={ref}
        className={cx("cw-v2-carousel__track", `cw-v2-carousel__track--${orientation}`, className)}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = "CarouselContent";

export const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CarouselItem({ className, ...props }, ref) {
    const { orientation } = useCarousel();
    return (
      <div
        ref={ref}
        aria-roledescription="slide"
        role="group"
        className={cx("cw-v2-carousel__item", `cw-v2-carousel__item--${orientation}`, className)}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = "CarouselItem";

export interface CarouselNavigationButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Position the button absolutely outside the track. Default true. */
  floating?: boolean;
}

export const CarouselPrevious = React.forwardRef<HTMLButtonElement, CarouselNavigationButtonProps>(
  function CarouselPrevious({ className, floating = true, ...props }, ref) {
    const { canScrollPrev, orientation, scrollPrev } = useCarousel();
    return (
      <button
        ref={ref}
        type="button"
        aria-label="Previous slide"
        className={cx(
          "cw-v2-button",
          "cw-v2-button--secondary",
          "cw-v2-button--md",
          "cw-v2-icon-button",
          "cw-v2-icon-button--md",
          "cw-v2-carousel__nav",
          "cw-v2-carousel__nav--prev",
          `cw-v2-carousel__nav--${orientation}`,
          floating && "cw-v2-carousel__nav--floating",
          className,
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        {orientation === "horizontal" ? (
          <ArrowLeft className="cw-v2-carousel__nav-icon" aria-hidden />
        ) : (
          <ArrowLeft
            style={{ transform: "rotate(90deg)" }}
            className="cw-v2-carousel__nav-icon"
            aria-hidden
          />
        )}
      </button>
    );
  },
);
CarouselPrevious.displayName = "CarouselPrevious";

export const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselNavigationButtonProps>(
  function CarouselNext({ className, floating = true, ...props }, ref) {
    const { canScrollNext, orientation, scrollNext } = useCarousel();
    return (
      <button
        ref={ref}
        type="button"
        aria-label="Next slide"
        className={cx(
          "cw-v2-button",
          "cw-v2-button--secondary",
          "cw-v2-button--md",
          "cw-v2-icon-button",
          "cw-v2-icon-button--md",
          "cw-v2-carousel__nav",
          "cw-v2-carousel__nav--next",
          `cw-v2-carousel__nav--${orientation}`,
          floating && "cw-v2-carousel__nav--floating",
          className,
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        {orientation === "horizontal" ? (
          <ArrowRight className="cw-v2-carousel__nav-icon" aria-hidden />
        ) : (
          <ArrowRight
            style={{ transform: "rotate(90deg)" }}
            className="cw-v2-carousel__nav-icon"
            aria-hidden
          />
        )}
      </button>
    );
  },
);
CarouselNext.displayName = "CarouselNext";

/** Pagination dots — one per slide, highlighting the active snap. */
export const CarouselDots = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CarouselDots({ className, ...props }, ref) {
    const { api, scrollSnaps, selectedIndex } = useCarousel();
    if (scrollSnaps.length === 0) return null;
    return (
      <div ref={ref} role="tablist" className={cx("cw-v2-carousel__dots", className)} {...props}>
        {scrollSnaps.map((snap, index) => {
          const isActive = index === selectedIndex;
          return (
            <button
              // biome-ignore lint/suspicious/noArrayIndexKey: scrollSnaps is a positional list owned by Embla; the index is the dot's stable identity (slides don't reorder).
              key={`carousel-dot-${snap}-${index}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={`Go to slide ${index + 1}`}
              className={cx("cw-v2-carousel__dot", isActive && "cw-v2-carousel__dot--active")}
              onClick={() => api?.scrollTo(index)}
            />
          );
        })}
      </div>
    );
  },
);
CarouselDots.displayName = "CarouselDots";

export type { CarouselApi };
